import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError, AxiosRequestConfig, isAxiosError } from 'axios';

import httpClient from '@api/httpClient';

export interface ApiErrorInterface {
	status: number;
	error: string;
}

export type UnknownError = {
	message?: string;
	status?: number;
	response?: { message?: string; status?: number; data?: { message?: string; status?: number } };
};

export interface ConfigRequestProps<ThunkArg> {
	requestPrefix: string;
	reqConfigCreator: (input?: ThunkArg) => AxiosRequestConfig;
	onSuccess?: (input?: ThunkArg) => void;
}

function configRequest<Returned, ThunkArg>({
	requestPrefix,
	reqConfigCreator,
	onSuccess,
}: ConfigRequestProps<ThunkArg>) {
	return createAsyncThunk<
		Returned,
		ThunkArg,
		{
			rejectValue: AxiosError;
		}
	>(requestPrefix, async (input, { rejectWithValue }) => {
		const reqConfig = reqConfigCreator(input);

		try {
			const { data } = await httpClient.request<Returned>(reqConfig);

			onSuccess?.(input);

			return data as Returned;
		} catch (e: unknown) {
			if (isAxiosError(e)) return rejectWithValue(e as AxiosError);
			else {
				if (typeof e === 'object' && e !== null) {
					const error = e as UnknownError;
					const message = error.message || error.response?.message || error.response?.data?.message || 'Unknown error';
					const code = String(error.status || error.response?.status || error.response?.data?.status || 'ERR_UNKNOWN');
					return rejectWithValue(new AxiosError(message, code));
				} else return rejectWithValue(new AxiosError('Unknown error'));
			}
		}
	});
}

export default configRequest;
